import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useFirestoreEvents, FirestoreEvent } from '../../hooks/useFirestoreEvents';
import { useCloudinaryUpload, CloudinaryImage } from '../../hooks/useCloudinaryUpload';

const CATEGORIES = [
  { value: 'Sports', label: 'Sports', color: 'brand-green', icon: '🏃‍♂️' },
  { value: 'Creative', label: 'Creative', color: 'brand-pink', icon: '🎨' },
  { value: 'Education', label: 'Education', color: 'brand-blue', icon: '🔬' },
  { value: 'Cultural', label: 'Cultural', color: 'brand-purple', icon: '🎭' },
  { value: 'Outdoor', label: 'Outdoor', color: 'brand-green', icon: '🌳' },
  { value: 'Literacy', label: 'Literacy', color: 'brand-pink', icon: '📚' },
];

const AdminEvents: React.FC = () => {
  const { events, loading, addEvent, updateEvent, deleteEvent } = useFirestoreEvents();
  const { uploadMultipleImages, uploading } = useCloudinaryUpload();

  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<FirestoreEvent | null>(null);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Sports');
  const [highlights, setHighlights] = useState<string[]>(['']);
  const [images, setImages] = useState<CloudinaryImage[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setTitle('');
    setDate('');
    setDescription('');
    setCategory('Sports');
    setHighlights(['']);
    setImages([]);
    setEditingEvent(null);
    setShowForm(false);
  };

  const handleEdit = (event: FirestoreEvent) => {
    setEditingEvent(event);
    setTitle(event.title);
    setDate(event.date);
    setDescription(event.description);
    setCategory(event.category);
    setHighlights(event.highlights.length > 0 ? event.highlights : ['']);
    setImages(event.images || []);
    setShowForm(true);
  };

  const handleDelete = async (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(eventId);
        toast.success('Event deleted successfully! 🗑️');
      } catch (error) {
        toast.error('Failed to delete event. Please try again.');
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const fileArray = Array.from(files);
      toast.info(`Uploading ${fileArray.length} image(s)... ⏳`);
      const uploadedImages = await uploadMultipleImages(fileArray, 'neev-events');
      setImages((prev) => [...prev, ...uploadedImages]);
      toast.success(`${fileArray.length} image(s) uploaded successfully! 📸`);
    } catch (error) {
      toast.error('Failed to upload images. Please try again.');
    }
  };

  const removeImage = (publicId: string) => {
    setImages((prev) => prev.filter((img) => img.public_id !== publicId));
  };

  const addHighlight = () => {
    setHighlights((prev) => [...prev, '']);
  };

  const updateHighlight = (index: number, value: string) => {
    const newHighlights = [...highlights];
    newHighlights[index] = value;
    setHighlights(newHighlights);
  };

  const removeHighlight = (index: number) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const selectedCategory = CATEGORIES.find((cat) => cat.value === category);
    if (!selectedCategory) return;

    const eventData = {
      title,
      date,
      description,
      category: selectedCategory.value,
      color: selectedCategory.color,
      icon: selectedCategory.icon,
      highlights: highlights.filter((h) => h.trim() !== ''),
      images,
    };

    try {
      if (editingEvent && editingEvent.id) {
        await updateEvent(editingEvent.id, eventData);
        toast.success('Event updated successfully! ✅');
      } else {
        await addEvent(eventData);
        toast.success('Event added successfully! 🎉');
      }
      resetForm();
    } catch (error) {
      toast.error('Failed to save event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Header */}
      <div className="bg-white shadow-brand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-dark">Events Management</h1>
          </div>
          <Link
            to="/admin/dashboard"
            className="text-brand-blue hover:text-brand-pink transition-colors"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Event Button */}
        {!showForm && (
          <motion.button
            onClick={() => setShowForm(true)}
            className="mb-8 bg-brand-pink text-white px-6 py-3 rounded-bubble font-bold hover:bg-opacity-90 transition-all"
            whileHover={{ y: -2 }}
          >
            + Add New Event
          </motion.button>
        )}

        {/* Event Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-3xl p-20 shadow-bubble mb-8"
            >
              <h2 className="text-2xl font-bold text-brand-dark mb-6">
                {editingEvent ? 'Edit Event' : 'Add New Event'}
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Title */}
                <div className="mb-4">
                  <label className="block text-brand-dark font-bold mb-2">Event Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-brand-blue rounded-bubble focus:outline-none focus:border-brand-pink"
                    required
                  />
                </div>

                {/* Date */}
                <div className="mb-4">
                  <label className="block text-brand-dark font-bold mb-2">Date *</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-brand-blue rounded-bubble focus:outline-none focus:border-brand-pink"
                    required
                  />
                </div>

                {/* Category */}
                <div className="mb-4">
                  <label className="block text-brand-dark font-bold mb-2">Category *</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-brand-blue rounded-bubble focus:outline-none focus:border-brand-pink"
                    required
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-brand-dark font-bold mb-2">Description *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-20 py-8 border-2 border-brand-blue rounded-bubble focus:outline-none focus:border-brand-pink"
                    rows={4}
                    required
                  />
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <label className="block text-brand-dark font-bold mb-2">Highlights</label>
                  {highlights.map((highlight, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={highlight}
                        onChange={(e) => updateHighlight(index, e.target.value)}
                        className="flex-1 px-4 py-2 border-2 border-brand-blue rounded-bubble focus:outline-none focus:border-brand-pink"
                        placeholder="Enter highlight"
                      />
                      {highlights.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeHighlight(index)}
                          className="px-4 py-2 bg-red-500 text-white rounded-bubble hover:bg-red-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addHighlight}
                    className="text-brand-blue hover:text-brand-pink font-bold"
                  >
                    + Add Highlight
                  </button>
                </div>

                {/* Images */}
                <div className="mb-6">
                  <label className="block text-brand-dark font-bold mb-2">Event Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-12 py-8 border-2 border-brand-blue rounded-bubble"
                    disabled={uploading}
                  />
                  {uploading && <p className="text-brand-blue mt-2">Uploading images...</p>}

                  {images.length > 0 && (
                    <div className="grid grid-cols-4 gap-10 mt-8">
                      {images.map((img) => (
                        <div key={img.public_id} className="relative group">
                          <img
                            src={img.thumbnail}
                            alt=""
                            className="w-full h-64 object-cover rounded-bubble"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-bubble flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeImage(img.public_id)}
                              className="opacity-0 group-hover:opacity-100 bg-red-500 text-white px-4 py-2 rounded-bubble font-bold hover:bg-red-600 transition-all"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={submitting || uploading}
                    className="bg-brand-pink text-white px-6 py-3 rounded-bubble font-bold hover:bg-opacity-90 disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : editingEvent ? 'Update Event' : 'Add Event'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-300 text-brand-dark px-6 py-3 rounded-bubble font-bold hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Events List */}
        <div className="bg-white rounded-3xl p-8 shadow-bubble">
          <h2 className="text-2xl font-bold text-brand-dark mb-6">All Events ({events.length})</h2>

          {loading ? (
            <p className="text-brand-blue">Loading events...</p>
          ) : events.length === 0 ? (
            <p className="text-brand-blue">No events yet. Add your first event!</p>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="border-2 border-brand-blue rounded-bubble p-16 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{event.icon}</span>
                      <h3 className="text-lg font-bold text-brand-dark">{event.title}</h3>
                    </div>
                    <p className="text-brand-blue text-sm mb-1">{event.date}</p>
                    <p className="text-brand-dark text-sm mb-2">{event.description.substring(0, 100)}...</p>
                    <p className="text-brand-blue text-xs">{event.images?.length || 0} images</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(event)}
                      className="bg-brand-blue text-white px-4 py-2 rounded-bubble hover:bg-opacity-90"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => event.id && handleDelete(event.id)}
                      className="bg-red-500 text-white px-4 py-2 rounded-bubble hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEvents;

